/**
 * Invoice tax block for the generation step and review modal.
 *
 * Mirrors logitrack_v2/utils/client_invoice_helpers.py (`split_gst`,
 * `build_tax_summary`). Preview and the generated PDF must agree to the paisa, so
 * any change here needs the same change there.
 *
 * GST has two axes: the line's SAC fixes the rate, the GSTIN state codes fix the
 * head split. Tax is computed per line and aggregated, never one rate over the
 * subtotal. Leaf module (no imports) so it can't create a cycle.
 */

/**
 * Round to 2dp, half-up, matching Decimal.quantize(ROUND_HALF_UP) on the backend.
 *
 * Rounded on the magnitude, then re-signed: Python's ROUND_HALF_UP takes a half
 * away from zero (-3.545 -> -3.55) while a bare Math.round takes it towards +inf
 * (-3.545 -> -3.54). Deduction lines carry negative amounts, so the two would
 * disagree by a paisa on the preview and the PDF without this.
 */
export const q2 = (value) => {
	const n = Number(value) || 0;
	const sign = n < 0 ? -1 : 1;
	return (sign * Math.round((Math.abs(n) + Number.EPSILON) * 100)) / 100;
};

/**
 * Tax borne by one line, split into heads. Each head is rounded independently, as
 * the backend does — 39.30 @ 18% intra-state is 3.54 + 3.54 = 7.08, not 7.07.
 */
export const computeLineTax = (amount, ratePercent, taxMode) => {
	const rate = (Number(ratePercent) || 0) / 100;
	const base = Number(amount) || 0;
	if (taxMode === 'NIL' || rate <= 0) {
		return { cgst: 0, sgst: 0, igst: 0, taxAmount: 0 };
	}
	if (taxMode === 'IGST') {
		const igst = q2(base * rate);
		return { cgst: 0, sgst: 0, igst, taxAmount: igst };
	}
	const cgst = q2((base * rate) / 2);
	const sgst = q2((base * rate) / 2);
	return { cgst, sgst, igst: 0, taxAmount: q2(cgst + sgst) };
};

/**
 * Group the taxable lines by their rate slab; exempt lines (rate 0) are skipped.
 *
 * A deduction row is fixed to the exempt SAC, so it never lands in a slab here —
 * it is netted off the taxable basis afterwards by `applyDeductions`.
 */
const groupBySlab = (items) => {
	const slabs = new Map();
	items.forEach((r) => {
		const rate = Number(r.tax_rate) || 0;
		if (rate <= 0) return;
		const slab = slabs.get(rate) || { basis: 0, cgst: 0, sgst: 0, igst: 0 };
		slab.basis += Number(r.amount) || 0;
		slab.cgst += Number(r.cgst) || 0;
		slab.sgst += Number(r.sgst) || 0;
		slab.igst += Number(r.igst) || 0;
		slabs.set(rate, slab);
	});
	return slabs;
};

/** Total discounted by the deduction rows, as a positive number. */
const deductionTotal = (items) =>
	(items || []).reduce((sum, r) => (r.is_deduction ? sum - (Number(r.amount) || 0) : sum), 0);

/**
 * Net the deductions off the taxable basis and re-derive the tax from what is left.
 *
 * The invoice is billed at one taxable rate (the SAC master carries 18% and Exempt),
 * so a deduction has exactly one basis to come off. Its own row stays exempt and
 * shows no tax; the reduction lands on the tax block, which is what the client is
 * actually charged. This is the one place tax is NOT the sum of the per-line heads,
 * so both this module and build_tax_summary in client_invoice_helpers.py must apply
 * it — otherwise the preview and the PDF would disagree.
 *
 * More than one taxable rate is refused upstream (a single deduction would have no
 * one basis to come off), so the reduction is applied to the only slab present.
 */
const applyDeductions = (slabs, items, taxMode) => {
	const deduction = deductionTotal(items);
	if (deduction <= 0 || slabs.size !== 1) return slabs;
	const [rate, slab] = [...slabs.entries()][0];
	const basis = q2(slab.basis - deduction);
	const { cgst, sgst, igst } = computeLineTax(basis, rate, taxMode);
	return new Map([[rate, { basis, cgst, sgst, igst }]]);
};

/** The heads at zero — printed when nothing on the invoice is taxable. */
const zeroHeadRows = (taxMode) =>
	(taxMode === 'IGST' ? ['IGST'] : ['CGST', 'SGST']).map((head) => ({
		key: head.toLowerCase(),
		tax_type: head,
		tax_code: head,
		basis: 0,
		rate: 0,
		amount: 0,
	}));

/** One IGST row, or one CGST + SGST pair, for a single rate slab. */
const slabRows = (rate, slab, taxMode) => {
	const basis = q2(slab.basis);
	if (taxMode === 'IGST') {
		return [
			{
				key: `igst-${rate}`,
				tax_type: 'IGST',
				tax_code: 'IGST',
				basis,
				rate,
				amount: q2(slab.igst),
			},
		];
	}
	return [
		{
			key: `cgst-${rate}`,
			tax_type: 'CGST',
			tax_code: 'CGST',
			basis,
			rate: rate / 2,
			amount: q2(slab.cgst),
		},
		{
			key: `sgst-${rate}`,
			tax_type: 'SGST',
			tax_code: 'SGST',
			basis,
			rate: rate / 2,
			amount: q2(slab.sgst),
		},
	];
};

/**
 * Rate-wise rows: [{ key, tax_type, tax_code, basis, rate, amount }].
 *
 * - `basis` is the taxable value only; exempt lines get no row of their own.
 * - A deduction is a discount on the supply, so it lowers the basis and the tax
 *   charged on it — printing tax on the pre-deduction value over-billed the client.
 * - Nothing taxable -> the heads still print at 0 basis / 0% / 0, never 'Nil'.
 * - 'Nil' is reserved for taxMode === 'NIL' (unregistered / B2C clients).
 */
export const buildTaxSummary = (rows, taxMode) => {
	const items = rows || [];

	if (taxMode === 'NIL') {
		const subtotal = items.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
		return [
			{
				key: 'nil',
				tax_type: 'GST',
				tax_code: 'Nil',
				basis: q2(subtotal),
				rate: 0,
				amount: 0,
			},
		];
	}

	const slabs = applyDeductions(groupBySlab(items), items, taxMode);
	if (!slabs.size) return zeroHeadRows(taxMode);

	return [...slabs.keys()]
		.sort((a, b) => b - a)
		.flatMap((rate) => slabRows(rate, slabs.get(rate), taxMode));
};

/**
 * Invoice-level totals.
 *
 * The heads are read back off the tax block rather than summed from the line rows,
 * because a deduction lowers the basis without changing any line's own tax — the
 * two only agree on an invoice that carries no deduction. Deduction rows hold a
 * negative amount, so the subtotal nets them by plain summation.
 */
export const buildTotals = (rows, taxMode) => {
	const subtotal = (rows || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
	const acc = { cgst: 0, sgst: 0, igst: 0 };
	applyDeductions(groupBySlab(rows || []), rows || [], taxMode).forEach((slab) => {
		acc.cgst += Number(slab.cgst) || 0;
		acc.sgst += Number(slab.sgst) || 0;
		acc.igst += Number(slab.igst) || 0;
	});
	const taxTotal = q2(acc.cgst + acc.sgst + acc.igst);
	return {
		subtotal: q2(subtotal),
		taxTotal,
		cgst: q2(acc.cgst),
		sgst: q2(acc.sgst),
		igst: q2(acc.igst),
		total: q2(q2(subtotal) + taxTotal),
	};
};

/** '9%' / '2.5%' / '18%' — trims trailing zeros, matching `format_rate` on the backend. */
export const formatRate = (rate) => `${Number(Number(rate || 0).toFixed(2))}%`;
